import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Dialog, DialogContent, Button, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Props, State } from "./interfaces";

import isEqual from "lodash/isEqual";

import DialogActions from "../../DialogActions/DialogActions";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogTitle from "../../DialogTitle/DialogTitle";
import { TnAWorkedDaysSTO } from "../../../models/tna/tna-worked-days/tna-worked-days-sto";
import { TnAWorkedDaysApiService } from "../../../services/tna-worked-days/tna-worked-days-api.service";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import MultiDatePicker from "../MultiselectCalendar/MultiselectCalendar";
import { CalendarPlusIcon } from "../../../assets/icons";
import CustomConfirmationChangesDialog from "src/containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { intlFormatDateShort } from "src/utils/time-utils";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { TnAWorkedDaysFilter } from "src/models/tna/tna-worked-days/tna-worked-days-filter";

export const ReportWorkedDays = ({
  employee,
  tnAMonth,
  dates,
  id,
  handleNotifyParent,
  buttonType = "icon",
  children,
  viewMode = false,
  dayData,
  customButtonStyle,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_worked_days";
  const theme = useTheme();

  //#region default worked days
  const DEFAULT_WORKED_DAYS: Partial<TnAWorkedDaysSTO> = useMemo(() => {
    return {
      id: id ?? "",
      isDeleted: false,
      companyAccount: null,
      legalEntity: employee?.legalEntity ?? null,
      employee: employee ?? null,
      project: null,
      client: null,
      dates: dates ?? [],
      tnAMonth: tnAMonth,
      isAuto: false,
    };
  }, [employee, dates, tnAMonth]);

  //#region initial state
  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      workedDays: DEFAULT_WORKED_DAYS as TnAWorkedDaysSTO,
      isCCDOpen: false,
      isWarningCCCDOpen: false,
      daysWithoutScheduleHours: [],
    };
  }, [DEFAULT_WORKED_DAYS]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change field
  const handleChangeField = useCallback(
    (field: keyof TnAWorkedDaysSTO, value: TnAWorkedDaysSTO[keyof TnAWorkedDaysSTO]) => {
      setState((state) => ({
        ...state,
        workedDays: {
          ...state.workedDays,
          [field]: value,
          isAuto: false,
        },
      }));
    },
    []
  );

  //#region load worked days for this month
  const loadWorkedDaysForCurrentMonth = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new TnAWorkedDaysApiService().loadItems({
        employeeId: employee.id,
        tnAMonth: tnAMonth,
      } as TnAWorkedDaysFilter);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState((state) => ({
          ...state,
          workedDays: response.items[0],
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [employee.id, tnAMonth]);

  useEffect(() => {
    if (!id) loadWorkedDaysForCurrentMonth();
  }, [employee, tnAMonth, id]);

  //#region change worked days
  const handleChangeWorkedDays = (days: Date[]) => {
    if (viewMode) return;

    if (!!days.length) {
      handleChangeField(
        "dates",
        days.map((day) => day.getDate())
      );
    } else {
      handleChangeField("dates", []);
    }
  };

  //#region save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await new TnAWorkedDaysApiService().saveItems([
        {
          ...state.workedDays,
          id: !!state?.workedDays?.id ? state.workedDays.id : uuidv4(),
          isAuto: false,
        } as TnAWorkedDaysSTO,
      ]);

      if (!response.error) {
        setState((state) => ({
          ...state,
          isDialogOpen: false,
          loading: false,
          isCCDOpen: false,
          isWarningCCCDOpen: false,
          daysWithoutScheduleHours: [],
        }));
        handleNotifyParent();
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [INITIAL_STATE, handleNotifyParent, state.workedDays]);

  //#region discard changes
  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  //#region reset and close dialog
  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.workedDays, INITIAL_STATE.workedDays)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  //region daysWithoutScheduleHours
  const daysWithoutScheduleHours = useMemo(() => {
    let data = (dayData as TnADayDataRef[]) ?? [];

    const result =
      data
        ?.filter(
          (item) =>
            item.tnADate?.year === tnAMonth.year && item.tnADate?.month === tnAMonth.month && !item?.expectedPresence
        )
        ?.map((item) => item.tnADate?.day) ?? [];

    return result;
  }, [dayData, tnAMonth]);

  //#region checkIfDayCanBeReported
  const checkIfDayCanBeReported = useCallback(() => {
    let result: number[] = [];

    state.workedDays.dates.forEach((item) => {
      if (daysWithoutScheduleHours.includes(item)) {
        result.push(item);
      }
    });

    return result;
  }, [daysWithoutScheduleHours, state.workedDays.dates]);

  return (
    <>
      <Button
        onClick={(e) => {
          e.preventDefault();
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        // variant="contained"
        title={`${t(`${tRP}.title`)}`}
        style={{
          color: theme.palette.primary.main,
          ...customButtonStyle,
        }}
        variant="contained"
      >
        {children ? (
          children
        ) : buttonType === "icon" ? (
          <CalendarPlusIcon />
        ) : buttonType === "iconAndText" ? (
          <>
            <Typography>Report worked days &nbsp; </Typography>
            <CalendarPlusIcon />
          </>
        ) : (
          <Typography>Report worked days </Typography>
        )}
      </Button>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={`${t(`${tRP}.title`)}`}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <MultiDatePicker
              currentYear={state.workedDays.tnAMonth?.year as number}
              currentMonth={(state.workedDays.tnAMonth?.month as number) - 1}
              selectedValues={state.workedDays.dates}
              handleChange={handleChangeWorkedDays}
            />
          </DialogContent>
          <DialogActions
            handleSave={() => {
              const daysNotAllowedToReport = checkIfDayCanBeReported();

              !!daysNotAllowedToReport.length
                ? setState((state) => ({
                    ...state,
                    isWarningCCCDOpen: true,
                    daysWithoutScheduleHours: daysNotAllowedToReport,
                  }))
                : handleSave();
            }}
            handleCancel={resetAndCloseDialog}
            handleDelete={() => handleChangeField("dates", [])}
            customDeleteName={"Remove all"}
            canBeDeleted={!viewMode}
            disabledSaveButton={viewMode}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />

      <CustomConfirmationChangesDialog
        open={state.isWarningCCCDOpen}
        loading={state.loading}
        title={"Outside of schedule"}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            isWarningCCCDOpen: false,
            daysWithoutScheduleHours: [],
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              handleSave();
            },
          },
        ]}
      >
        <Grid>
          {!!state.daysWithoutScheduleHours.length && (
            <Grid sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6">According to the schedule, these are not working days:</Typography>

                <ul>
                  {state.daysWithoutScheduleHours
                    .sort((a, b) => a - b)
                    .map((day) => {
                      return (
                        <li key={day}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            {intlFormatDateShort(new Date(tnAMonth.year, tnAMonth.month - 1, day))}
                          </Typography>
                        </li>
                      );
                    })}
                </ul>
              </Grid>

              <Typography variant="h6">Are you sure you want to report them?</Typography>
            </Grid>
          )}
        </Grid>
      </CustomConfirmationChangesDialog>
    </>
  );
};
